import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { compact } from 'lodash-es';

import { ColorConstants } from '@celum/common-components/constants';
import { CelumIcon, IconConfiguration } from '@celum/common-components/icon';
import { ICON_SIZE } from '@celum/core/util';
import { SanitizedPipe } from '@celum/ng2base/pipes';

export type MessageBoxType = 'custom' | 'info' | 'warn' | 'error' | 'success';

@Component({
  selector: 'message-box',
  templateUrl: './message-box.html',
  styleUrls: ['./message-box.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, CelumIcon, SanitizedPipe],
  host: {
    class: 'type()',
    '[style.--messageBoxColor]': 'color()'
  }
})
export class MessageBox {
  /** Define the type of the message box (defines colors). Default is `info`. */
  public type = input<MessageBoxType>('info');
  /** Optionally define a custom icon to show. By default, the icon is set based on the {@link type} defined. */
  public icon = input<string>('');
  /** Define the text to show in the message box. */
  public text = input<string>('');
  /** Optionally define a color that will be used if {@link type} is set to custom. Can either be a hex or rgb value (no rgba) - also works if set via css variable. */
  public customColor = input<string>('');
  /** Optionally define whether the icon and text will be centered inside the message box. Default is `false`. */
  public centered = input<boolean>(false);
  /** Optionally define how the passed text content is aligned. */
  public textContentAlignment = input<'space-between' | 'normal'>('normal');
  /** Optionally define the density of the message box. Default is `default`. */
  public density = input<'default' | 'compact'>('default');

  protected readonly internalIcon = computed(() => {
    const type = this.type();
    const icon = this.icon();
    const iconToUse = icon ? icon : type === 'warn' ? 'attention-m' : type === 'error' ? 'error-m' : type === 'success' ? 'check-circle-m' : 'info-icon';
    const iconSize = this.density() === 'default' ? ICON_SIZE.m : ICON_SIZE.s;
    return IconConfiguration.medium(iconToUse).withIconSize(iconSize).withColor(this.color());
  });

  protected readonly color = computed(() => {
    const colorByType = {
      info: ColorConstants.PRIMARY,
      warn: ColorConstants.WARNING,
      error: ColorConstants.ERROR,
      success: ColorConstants.SUCCESS,
      custom: this.customColor() || ColorConstants.PRIMARY
    };
    return colorByType[this.type()];
  });
  protected readonly compact = compact;
}
